/**
 * Steam 分版本分析下载配置
 * @description 版本分析列表数据下载配置
 */
import { DownloadConfigInfoType, CommonColumnsType } from '../types';
import {
  getVersionAnalysisList,
  getVersionTimeline,
} from '@op/services/steam/versionAnalysis';
import { DBO_TM } from '@op/utils/format';
import { calculateSteamReviewRate } from '@op/utils/text';
import { CHART_METRIC_OPTIONS } from '@op/pages/SteamPerformance/VersionAnalysis/List/constants';
import { getVersionConfig } from '@op/pages/SteamPerformance/VersionAnalysis/config';
import { initialUpperCase } from '@op/utils';
import orderBy from 'lodash-es/orderBy';

const METRIC_COLUMNS = [
  { key: 'number_of_reviews', header: 'Number of Reviews' },
  { key: 'positive_reviews', header: 'Positive Reviews' },
  { key: 'positive_ratio', header: 'Positive Ratio' },
  { key: 'negative_reviews', header: 'Negative Reviews' },
  { key: 'negative_ratio', header: 'Negative Ratio' },
];

/** 单次请求页大小（与服务端约定保持一致） */
const PAGE_SIZE = 100;

const pickList = (res: any): any[] => res?.data?.list || res?.list || [];
const pickTotal = (res: any): number => res?.data?.total || res?.total || 0;

/** 时间区间渲染：start_time ~ end_time（与表格 Time Interval 列保持一致） */
const renderTimeInterval = (item: any): string => {
  if (!item?.start_time || !item?.end_time) return '-';
  return DBO_TM.timeTitle({
    start_time: item.start_time,
    end_time: item.end_time,
    date_type: 'daily',
  });
};

/** Rating 渲染：仅输出文本（如 "Mostly Positive"），与表格 Rating 列空值规则一致 */
const renderRatingText = (item: any): string => {
  const value = item?.positive_ratio;
  if (
    value === null ||
    value === undefined ||
    isNaN(value) ||
    item?.number_of_reviews === 0
  ) {
    return '-';
  }
  return calculateSteamReviewRate(value);
};

export const VersionAnalysisDownload: DownloadConfigInfoType = {
  type: 'xlsx',
  sheetNames: 'Version Analysis',

  columns: ({ auxiliaryParams, filter }) => {
    const { visibleColumns, isConfigGame, groupBy } = auxiliaryParams;
    const cols: any[] = [];

    if (isConfigGame) {
      const versionConfig = getVersionConfig(filter?.edition_unified_id);
      if (versionConfig) {
        versionConfig.levels.forEach((level, index) => {
          const showFor = versionConfig.levels.slice(index).map(l => l.value);
          if (showFor.includes(groupBy)) {
            cols.push({ key: level.value, header: level.label });
          }
        });
      }
    } else {
      cols.push(
        { key: 'update_title_en', header: 'Update Title (EN)' },
        { key: 'update_title_cn', header: 'Update Title (CN)' },
      );
    }

    cols.push({
      key: 'start_time',
      header: 'Time Interval',
      renderHelper: [renderTimeInterval],
    });

    cols.push({ key: 'release_date', header: 'Release Date' });

    if (visibleColumns?.includes('rating')) {
      cols.push({
        key: 'positive_ratio',
        header: 'Rating',
        renderHelper: [renderRatingText],
      });
    }

    METRIC_COLUMNS.forEach(col => {
      if (visibleColumns?.includes(col.key)) {
        cols.push(col);
      }
    });

    return cols;
  },

  downloadFunc: async filter => {
    const requestPage = (page: number) =>
      getVersionAnalysisList({
        data: { ...filter, page, page_size: PAGE_SIZE },
      });

    let page = 1;
    const firstRes = await requestPage(page);
    let list: any[] = pickList(firstRes);
    const total = pickTotal(firstRes);

    // 后端分页：total 大于已拉取记录时继续拉下一页；命中安全上限或后端无更多数据时停止
    while (total > page * PAGE_SIZE) {
      page += 1;
      const nextRes = await requestPage(page);
      const nextList = pickList(nextRes);
      if (nextList.length === 0) break;
      list = list.concat(nextList);
    }

    return list;
  },
};

/* ==========================================================================
 *  板块 D — 版本对比图表下载配置
 *  ======================================================================== */

/**
 * 版本对比图表下载配置
 * @description 每个指标生成一个独立的 Sheet。
 *   非占比：版本号 | SUM | D1 | … | DN
 *   占比  ：版本号 | D1  | … | DN（D 列自动百分比格式）
 */
export const VersionCompareChartDownload: DownloadConfigInfoType = {
  type: 'xlsx',
  sheetNames: CHART_METRIC_OPTIONS.map(m => ({
    name: m.text,
    key: m.value,
  })) as any,

  /** 每 Sheet 独立列定义（getRealColumns 已合并 params + sheet，key 为指标值） */
  columns: ({ auxiliaryParams }, sheetInfo): CommonColumnsType => {
    const {
      maxDays = 0,
      versionSpan,
      isConfigGame,
      groupBy,
      trendType,
    } = auxiliaryParams || {};
    const { key } = sheetInfo || {};
    // 解析 versionSpan 范围，如 "1-10" 表示只导出 D1-D10 列
    let colStart = 1;
    let colEnd = maxDays;
    if (versionSpan && versionSpan !== '-') {
      const parts = versionSpan.split('-');
      if (parts.length === 2) {
        const s = Number(parts[0]);
        const e = Number(parts[1]);
        if (!isNaN(s) && !isNaN(e)) {
          colStart = Math.max(1, Math.min(s, e));
          colEnd = Math.min(maxDays, Math.max(s, e));
        }
      }
    }

    const isPercent =
      CHART_METRIC_OPTIONS.find(m => m.value === key)?.isPercent ?? false;

    const cols: CommonColumnsType = isConfigGame
      ? [{ key: groupBy, header: initialUpperCase(groupBy) }]
      : [
          { key: 'title_en', header: 'Version (EN)' },
          { key: 'title_cn', header: 'Version (CN)' },
        ];
    // 仅非占比指标展示 SUM 列
    if (!isPercent && trendType !== 'cumulative') {
      cols.push({ key: 'sum', header: 'SUM' });
    }
    for (let i = colStart; i <= colEnd; i++) {
      cols.push({
        key: `D${i}`,
        header: `D${i}`,
        ...(isPercent ? { numFmt: '0.00%' } : {}),
      });
    }
    return cols;
  },

  /** 依次请求每个指标的时间线数据 */
  downloadFunc: async filter => {
    const results: Record<string, any> = {};
    for (const metric of CHART_METRIC_OPTIONS) {
      const res = await getVersionTimeline({
        data: {
          ...filter,
          metric_item: metric.value,
          steam_filter_type: 'steam',
        },
      });
      results[metric.value] = res;
    }
    return results;
  },

  /** 将原始响应转换为每 Sheet 的行数据 */
  dealWithDataFunc: ({ data, auxiliaryParams, filter }) => {
    const { versionSpan } = auxiliaryParams || {};
    const versionConfig = getVersionConfig(filter?.edition_unified_id);

    // 解析 versionSpan 范围，与 columns 逻辑保持一致
    let colStart = 1;
    let colEnd = Infinity; // 由每个指标的 maxDay 兜底
    if (versionSpan && versionSpan !== '-') {
      const parts = versionSpan.split('-');
      if (parts.length === 2) {
        const s = Number(parts[0]);
        const e = Number(parts[1]);
        if (!isNaN(s) && !isNaN(e)) {
          colStart = Math.max(1, Math.min(s, e));
          colEnd = Math.max(s, e);
        }
      }
    }

    const result: Record<string, any[]> = {};

    for (const metric of CHART_METRIC_OPTIONS) {
      const metricResponse = data?.[metric.value];
      if (!metricResponse) continue;

      const series = metricResponse?.series || [];
      const maxDay = metricResponse?.max_day || 0;
      const actualEnd = Math.min(colEnd, maxDay);
      const isPercent = metric.isPercent;

      // 按 release_date 逆序排序
      const sortedSeries = orderBy(
        series,
        [(item: any) => item.release_date || ''],
        ['desc'],
      );

      const rows = sortedSeries.map((item: any) => {
        const row: Record<string, any> = {
          title_en: item?.update_title_en ?? '',
          title_cn: item?.update_title_cn ?? '',
        };
        // 配置游戏：按 versionConfig.levels 动态写入维度字段
        if (versionConfig) {
          versionConfig.levels.forEach(level => {
            row[level.value] = item?.[level.value] ?? '';
          });
        }
        const dataPoints: any[] = item?.data || [];

        if (!isPercent) {
          let sum = 0;
          for (let i = colStart; i <= actualEnd; i++) {
            const point = dataPoints.find((p: any) => p.day === `D${i}`);
            const val = point?.value ?? null;
            row[`D${i}`] = val;
            if (val !== null && val !== undefined && !isNaN(val)) {
              sum += val;
            }
          }
          row.sum = sum;
        } else {
          for (let i = colStart; i <= actualEnd; i++) {
            const point = dataPoints.find((p: any) => p.day === `D${i}`);
            // 占比源数据为 0-1 小数，写入原值，列级 numFmt 负责百分比显示
            row[`D${i}`] = point?.value ?? null;
          }
        }

        return row;
      });

      result[metric.value] = rows;
    }

    return result as any;
  },
};
